import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { REVELATION } from '../../utils/constants';
import { fadeUp, spring, viewportOnce } from '../../utils/animations';

const chipVariants = {
  hidden: { opacity: 0, scale: 0.6, filter: 'blur(8px)' },
  visible: { opacity: 1, scale: 1, filter: 'blur(0px)' },
};

export default function Revelation() {
  const [ref, inView] = useInView(viewportOnce);
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const glowScale = useTransform(scrollYProgress, [0.2, 0.6], [0.8, 1.2]);
  const glowOpacity = useTransform(scrollYProgress, [0.1, 0.4, 0.8], [0, 0.25, 0]);

  return (
    <section ref={sectionRef} className="section-padding relative overflow-hidden">
      {/* Breathing glow — scroll-linked */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-radial-purple gpu"
        style={{ scale: glowScale, opacity: glowOpacity }}
      />

      <div className="container-custom relative z-10" ref={ref}>
        {/* Headline */}
        <motion.div
          className="text-center mb-16"
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          transition={spring}
        >
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-4">
            <span className="bg-gradient-brand bg-clip-text text-transparent">
              {REVELATION.headline}
            </span>
          </h2>
          <p className="text-xl text-white/40">{REVELATION.subheadline}</p>
        </motion.div>

        {/* Signal chips — staggered spring entrance with blur */}
        <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
          {REVELATION.signals.map((signal, i) => (
            <motion.div
              key={signal}
              className="group relative"
              variants={chipVariants}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              transition={{
                ...spring,
                delay: 0.4 + i * 0.06,
              }}
            >
              <div className="relative px-5 py-3 rounded-full border border-white/[0.08] bg-white/[0.03] hover:border-brand-purple/40 hover:bg-brand-purple/[0.06] transition-colors duration-300 cursor-default gpu">
                <span className="relative text-sm font-medium text-white/60 group-hover:text-white/90 transition-colors duration-300">
                  {signal}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Subtext */}
        <motion.p
          className="text-center text-white/25 text-sm mt-12 max-w-md mx-auto"
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          transition={{ ...spring, delay: 1.2 }}
        >
          Each one measured. Each one scored. Each one improvable.
        </motion.p>
      </div>
    </section>
  );
}
